import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Loading, Typogaphy } from 'vnetwork-ui'
import { Copy } from 'lucide-react'
import toast from 'react-hot-toast'
import Paper from '@/components/Paper'
import { DataTableCustomized } from '@/components/ui/data-table-customized'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { getReferredCustomersColumns } from '@/modules/Affiliate/utils/referred-customers-columns'
import {
    getCurrentEmployeeReferralStats,
    getCurrentEmployeeReferralCustomers,
} from '../services'

const formatVND = (n: number) =>
    new Intl.NumberFormat('vi-VN', {
        style: 'currency',
        currency: 'VND',
        maximumFractionDigits: 0,
    }).format(n || 0)

const StatCard = ({ label, value }: { label: string; value: string }) => (
    <div className="p-4 bg-white rounded-lg border border-gray-100">
        <Typogaphy size="small" className="text-gray-500">
            {label}
        </Typogaphy>
        <Typogaphy size="medium" className="font-semibold mt-1">
            {value}
        </Typogaphy>
    </div>
)

// ReferralSummary is the logged-in employee's own referral dashboard (link + stats + referred
// customers), analogous to the customer affiliate dashboard on the partner console.
const ReferralSummary = () => {
    const { t } = useTranslation('affiliate')
    const [page, setPage] = useState(1)
    const [pageSize, setPageSize] = useState(10)

    const { data: statsData, renderState } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.EMPLOYEE_REFERRAL_LINK, 'me', 'stats'],
        queryFn: getCurrentEmployeeReferralStats,
    })
    const stats = statsData?.result

    const { data: custData } = useQueryWithHandler({
        queryKey: [
            QUERY_KEYS.EMPLOYEE_REFERRAL_LINK,
            'me',
            'customers',
            page,
            pageSize,
        ],
        queryFn: () =>
            getCurrentEmployeeReferralCustomers({
                page,
                page_size: pageSize,
            }),
        placeholderData: true,
    })
    const customers = custData?.results ?? []
    const total = custData?.pagination?.total ?? 0
    const columns = useMemo(() => getReferredCustomersColumns(t), [t])

    const state = renderState()
    if (state) return state
    if (!stats) return <Loading.FadingDots />

    const copyLink = () => {
        navigator.clipboard.writeText(stats.referral_link)
        toast.success(t('copied_to_clipboard'))
    }

    return (
        <div className="flex flex-col gap-4 mt-2">
            <Typogaphy size="medium" className="font-semibold">
                {t('referral')}
            </Typogaphy>

            <Paper className="flex items-center gap-3">
                <div className="min-w-0">
                    <Typogaphy size="small" className="text-gray-500">
                        {t('referral_link')}
                    </Typogaphy>
                    <Typogaphy size="small" className="font-medium truncate">
                        {stats.referral_link}
                    </Typogaphy>
                </div>
                <button
                    type="button"
                    onClick={copyLink}
                    title={t('copy')}
                    className="ml-auto shrink-0 text-primary hover:opacity-80"
                >
                    <Copy className="w-4 h-4" />
                </button>
            </Paper>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatCard
                    label={t('total_clicks')}
                    value={String(stats.total_clicks)}
                />
                <StatCard
                    label={t('converted_clicks')}
                    value={String(stats.total_conversions)}
                />
                <StatCard
                    label={t('referred_customers')}
                    value={String(stats.referred_customers)}
                />
                <StatCard
                    label={t('conversion_rate')}
                    value={`${stats.conversion_rate}%`}
                />
                {stats.earns_commission && (
                    <>
                        <StatCard
                            label={t('confirmed_commission')}
                            value={formatVND(stats.confirmed_commission)}
                        />
                        <StatCard
                            label={t('pending_commission')}
                            value={formatVND(stats.pending_commission)}
                        />
                    </>
                )}
            </div>

            <Paper>
                <DataTableCustomized
                    columns={columns}
                    rows={customers}
                    rowKey={(row) => row.customer_id}
                    size="small"
                    pagination={{
                        page,
                        pageSize,
                        total,
                        onPageChange: setPage,
                        onPageSizeChange: (size: number) => {
                            setPageSize(size)
                            setPage(1)
                        },
                    }}
                />
            </Paper>
        </div>
    )
}

export default ReferralSummary
